import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { BooleanReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/boolean/index.tsx';
import { BooleanSelect } from '@atlassian/jira-servicedesk-insight-boolean-select/src/ui/index.tsx';
import {
	useBooleanValue,
	getBooleanAttributeValue,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { EditViewSpacers } from '../../common/ui/edit-view-spacers/styled.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: DefaultAttributeType;
};

const BooleanAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useBooleanValue(attribute);

	const onValueChange = useCallback(
		({ value: updatedValue }: { value: boolean }) => {
			setValue(updatedValue);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getBooleanAttributeValue(attribute)),
		[attribute, setValue],
	);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.boolean-attribute"
			value={value === null ? null : String(value)}
			onChange={onValueChange}
			storedValue={
				getBooleanAttributeValue(attribute) === null
					? null
					: String(getBooleanAttributeValue(attribute))
			}
			isEditable={isEditable}
			attributeType={attributeType}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<BooleanReadView value={value} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<EditViewSpacers.BooleanSelect>
					<BooleanSelect
						autoFocus
						defaultMenuIsOpen
						value={value}
						validationState={hasValidationError ? 'error' : 'default'}
						onChange={onChange}
					/>
				</EditViewSpacers.BooleanSelect>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const BooleanEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightInlineEditableBooleanAttribute"
		packageName="jiraServicedeskInsightEditableBooleanAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<BooleanAttribute {...props} />
	</JSErrorBoundary>
);
